import React, { useState } from "react";

import { buildProductQuery } from "src/components/Content/Product/ProductQueries";
import BrandDetails from "src/components/Content/Brands/BrandDetails";
import { CalculatorButton } from "src/components/Calculator/components/CalculatorButton";
import MainLayout from "../../src/components/Layout/MainLayout";
import ProductFeedWithFilter from "src/components/Content/Product/ProductFeedWithFilter";
import Text from "src/components/Content/Text/Text";
import { TheCycleverseTheme } from "src/theme";
import brandList from "src/components/Content/Brands/highlightBrands";
import { createFetcher } from "src/lib/fetcher";
import environment from "src/environment";
import { createPrefetchFeedAndTextQuery } from "src/graphql/queries";
import { handleScroll } from "src/lib/handleScroll";
import { makeStyles } from "@material-ui/core/styles";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";

type PrefetchecContent = {
  textDetailImage: any;
  textDetailTextImage: any;
  textDetailText: any;
  textDetailArticles: any;
  textDetailOutro: any;
  productFeed: any;
};

const textDetailImageId = "brand-detail-image";
const textDetailTextImageId = "brand-detail-text-image";
const textDetailTextId = "brand-detail-text";
const textDetailArticlesId = "brand-detail-articles";
const textDetailOutroId = "brand-detail-outro";

const getTextKey = (key: string, genericString: string, locale: string) => {
  return `${key}-${genericString}-${locale}`;
};

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  brandDetailsContainer: {
    width: "90%",
    margin: "auto",
    marginBottom: "64px",
  },

  text: {
    ...theme.formText,
    width: "55%",
    maxWidth: theme.widths.contentWidth,
    margin: "auto",
    "@media (max-width: 768px)": {
      width: "100%",
    },
  },

  paragraphsContainer: {
    ...theme.formText,
    width: "55%",
    maxWidth: theme.widths.contentWidth,
    margin: "auto",
    "@media (max-width: 768px)": {
      width: "100%",
    },
  },

  headerImage: {
    width: "100%",
    "& img": {
      width: "100%",
      maxHeight: "500px",
      marginTop: "-14px",
      objectFit: "cover",

      "@media (max-width: 600px)": {
        height: "320px",
      },
    },
  },

  headerContainer: {
    maxWidth: "100%",
    margin: "auto",
    minHeight: "94vh",
  },

  titleOnBrandDetailsPage: {
    fontWeight: 900,
    lineHeight: "1.1",
    fontSize: 16,
    marginBottom: theme.spacing(1),
  },

  brandDetailsCard: {
    width: "97%",
  },

  showProductsButton: {
    marginTop: 40,
    marginBottom: 80,
  },

  titleAlternate: {
    textAlign: "center",
    fontSize: 36,
    marginTop: 0,
    fontFamily: theme.highlightFontFamily,
    fontWeight: "bold",
    lineHeight: "37px",
  },

  titleAlternateContainer: {
    marginTop: 50,
    marginBottom: 50,
    margin: "auto",
    maxWidth: 500,
  },
}));

const BrandDetailsPage = (props) => {
  const {
    textDetailImage,
    textDetailTextImage,
    textDetailText,
    textDetailArticles,
    textDetailOutro,
    productFeed,
  } = props.prefetchedContent;
  const classes = useStyles();
  const { t, lang: locale } = useTranslation("common");
  const router = useRouter();
  const { generic } = router.query;
  const genericString: string = decodeURIComponent(generic.toString());
  const [isContentSet, setIsContentSet] = useState();

  const onApplyCategoryFilter = (params) => {
    const brands = (params.brands ? params.brands : []).join(",");
    const sorting = params.sorting ? params.sorting : "";
    const condition = params.condition ? params.condition : "";
    const price = (params.priceRange ? params.priceRange : []).join(",");
    const categories = params?.categories ? params.categories : [];
    const search = params.searchString ? params.searchString : "";

    const query = {
      b: brands,
      o: sorting,
      d: condition,
      p: price,
      c: encodeURIComponent(categories.join(",")),
      s: search,
    };

    router.push(
      {
        pathname: "/[...generic]",
        query,
      },
      {
        pathname: `/${typeof generic == "string" ? generic : generic.join("/")}`,
        query,
      },
      { shallow: true, locale }
    );

    if (brands?.toLowerCase() === genericString?.toLowerCase()) {
      router.push(
        {
          pathname: "/marken/[generic]",
          query,
        },
        {
          pathname: `/marken/${typeof generic == "string" ? generic : generic.join("/")}`,
          query,
        },
        { shallow: true, locale }
      );
    } else {
      router.push(
        {
          pathname: "/products/search",
          query,
        },
        {
          pathname: "/products/search",
          query,
        },
        { shallow: true, locale }
      );
    }
  };

  return (
    <MainLayout contentClassName={classes.headerContainer}>
      <>
        <Text
          disableImageLazyLoad
          prefetchedContent={textDetailImage}
          buttonText="Headerbild bearbeiten"
          className={classes.headerImage}
          id={getTextKey(textDetailImageId, genericString, locale)}
        />

        <div className={classes.brandDetailsContainer}>
          <Text
            disableImageLazyLoad
            prefetchedContent={textDetailTextImage}
            buttonText="Logo bearbeiten"
            className={classes.text}
            id={getTextKey(textDetailTextImageId, genericString, locale)}
          />
          <BrandDetails brandName={genericString} />
          {(textDetailText || isContentSet) && (
            <div className={classes.showProductsButton}>
              <CalculatorButton text={t("marken:scrollToProducts")} scroll idToScrollTo="ProductFeed" />
            </div>
          )}
          <Text
            prefetchedContent={textDetailText}
            buttonText="Introtext bearbeiten"
            className={classes.text}
            id={getTextKey(textDetailTextId, genericString, locale)}
            isContentSet={(v) => {
              setIsContentSet(v);
            }}
          />
          <Text
            prefetchedContent={textDetailArticles}
            buttonText="Artikel bearbeiten"
            id={getTextKey(textDetailArticlesId, genericString, locale)}
            titleClass={classes.titleOnBrandDetailsPage}
            cardContainerClass={classes.brandDetailsCard}
          />
          {isContentSet && (
            <div className={classes.titleAlternateContainer} id="ProductFeed">
              <h2 className={classes.titleAlternate}>{t("marken:titleAlternate", { brandName: genericString })}</h2>
            </div>
          )}
          <ProductFeedWithFilter
            prefetchedContent={productFeed}
            disableLazyLoad={true}
            onApplyFilter={onApplyCategoryFilter}
            selectedBrand={generic}
            onNext={() => {
              handleScroll("ProductFeed", "auto");
            }}
            onPrev={() => {
              handleScroll("ProductFeed", "auto");
            }}
          />
          <Text
            prefetchedContent={textDetailOutro}
            buttonText="Outrotext bearbeiten"
            id={getTextKey(textDetailOutroId, genericString, locale)}
            className={classes.text}
            titleClass={classes.titleOnBrandDetailsPage}
            cardContainerClass={classes.brandDetailsCard}
          />
        </div>
      </>
    </MainLayout>
  );
};

export async function getStaticProps({ locale, params }) {
  const fetcher = createFetcher();

  const brand = params.generic;

  const textIds = [
    getTextKey(textDetailImageId, brand, locale),
    [getTextKey(textDetailTextImageId, brand, locale)],
    [getTextKey(textDetailTextId, brand, locale)],
    [getTextKey(textDetailArticlesId, brand, locale)],
    [getTextKey(textDetailOutroId, brand, locale)],
  ];

  const productQuery = buildProductQuery({ selectedBrand: brand, stepSize: 40, offset: 0 });

  const data = await fetcher([createPrefetchFeedAndTextQuery(textIds), JSON.stringify(productQuery)]);

  const prefetchedContent: PrefetchecContent = {
    textDetailImage: data?.text0?.content ?? null,
    textDetailTextImage: data?.text1?.content ?? null,
    textDetailText: data?.text2?.content ?? null,
    textDetailArticles: data?.text3?.content ?? null,
    textDetailOutro: data?.text4?.content ?? null,
    productFeed: data?.queryProducts ?? null,
  };
  return {
    props: {
      prefetchedContent,
      canonicalPath: `/${locale}/marken/${encodeURIComponent(brand)}`,
      key: brand, // force state reset for dynamic route
    },
    revalidate: environment.features.prerenderBrands ? 120 : 1,
  };
}

export async function getStaticPaths() {
  const paths = brandList.map((brandName) => {
    return {
      params: {
        id: brandName,
        generic: brandName,
      },
      locale: "de",
    };
  });
  return {
    paths,
    fallback: "blocking",
  };
}

export default BrandDetailsPage;
